import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { SCRUM_MASTER_TRIGGERS } from '../../lib/agileAiOrchestration';

/**
 * AI Scrum Master configuration panel (MAD + event triggers).
 */
export default function AiScrumMasterConfig({
  settings,
  onChange,
  onSave,
  saving = false,
  canEditMad = true,
  readOnly = false,
}) {
  const patch = (partial) => onChange({ ...settings, ...partial });

  const patchTrigger = (triggerId, checked) => {
    onChange({
      ...settings,
      triggers: { ...settings.triggers, [triggerId]: checked },
    });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mx: 1.5,
        mt: 1,
        mb: 0,
        p: 2,
        flexShrink: 0,
        borderColor: 'primary.main',
        bgcolor: (t) =>
          t.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : 'primary.50',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <SmartToyOutlinedIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>
          AI Scrum Master Configuration
        </Typography>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            MASTER ARCHITECTURE DOCUMENT (MAD)
          </Typography>
          <Tooltip
            title="The Scrum Master updates this document automatically. It is injected into every Developer AI's prompt to ensure context sharing between modular scripts."
            arrow
            placement="top"
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Tooltip>
        </Stack>
        <TextField
          value={settings.masterArchitectureDocument || ''}
          onChange={(e) => patch({ masterArchitectureDocument: e.target.value })}
          fullWidth
          multiline
          minRows={6}
          maxRows={16}
          placeholder="Shared architecture context for the agent swarm — updated by the Scrum Master as sprints evolve…"
          disabled={readOnly || !canEditMad}
          InputProps={{
            readOnly: readOnly || !canEditMad,
            sx: {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.82rem',
              bgcolor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(0,0,0,0.25)' : 'grey.100',
            },
          }}
          helperText={
            canEditMad ?
              'Machine-managed shared memory. Admins can edit manually when needed.' :
              'Only company admins can edit the Master Architecture Document.'
          }
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 1 }}>
          SCRUM MASTER TRIGGERS
        </Typography>
        <Stack spacing={0.5}>
          {SCRUM_MASTER_TRIGGERS.map((trigger) => (
            <FormControlLabel
              key={trigger.id}
              control={
                <Switch
                  size="small"
                  checked={Boolean(settings.triggers?.[trigger.id])}
                  onChange={(e) => patchTrigger(trigger.id, e.target.checked)}
                  disabled={readOnly}
                />
              }
              label={
                <Typography variant="body2">{trigger.label}</Typography>
              }
            />
          ))}
        </Stack>
      </Box>

      {!readOnly && (
        <Button
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
          disabled={saving}
          sx={{ fontWeight: 700 }}
        >
          {saving ? 'Saving…' : 'Save AI orchestration'}
        </Button>
      )}
    </Paper>
  );
}
